import classNames from 'classnames'
import { useState, type Dispatch, type SetStateAction } from 'react'

import { type TEditMode, type IEditAdmin, type IEditRole } from '@/pages/Admins/helper'
import { Button, CustomField, Icon } from '@/shared'
import { type IUpdateAdminDto } from '@App/types/adminsTypes'
import { EIcons } from '@App/types/iconType'
import { ROLE_MAPPER } from '@Config/config'

import style from './Item.module.scss'
import { Roles } from './Roles/Roles'
import { useFields } from './useFields'

interface IProps extends IEditAdmin {
    index: number
    onAddAdminRole: (login: string, roleName: string, onSuccess: () => void) => void
    onRemoveAdminRole: (login: string, roleName: string, onSuccess: () => void) => void
    setState: Dispatch<SetStateAction<IEditAdmin[]>>
    onUpdateAdminCallback: (dto: IUpdateAdminDto) => void
    setRemovableAdmin: (removableAdmin: string | null) => void
    isFetchingAdmins: boolean
}

interface IAction {
    title: string
    callback: () => void
    className: 'save' | 'chancgePassword' | 'chancgeRoles' | 'remove'
    disabled: boolean
    isVisible: boolean
}

export const Item = (props: IProps) => {
    const {
        login: loginVal,
        index,
        onAddAdminRole,
        onRemoveAdminRole,
        setState,
        editMode,
        onUpdateAdminCallback,
        setRemovableAdmin,
        isFetchingAdmins,
        roles
    } = props

    const {
        login,
        loginError,
        onChangeLogin,
        onTouchLogin,
        newPassword,
        newPasswordError,
        onChangeNewPassword,
        onTouchNewPassword,
        confirmPassword,
        confirmPasswordError,
        onChangeConfirmPassword,
        onTouchConfirmPassword,
        isValidFields,
        onInitializeFields,
        onZeroNewPassword,
        onZeroConfirmPassword
    } = useFields({ loginVal, editMode })

    const rolesList = Object.values(ROLE_MAPPER)

    rolesList.shift()

    rolesList.pop()

    const [adminRoles, setAdminRoles] = useState<IEditRole[]>(
        rolesList.map((item) => ({
            ...item,
            isCheched: roles.some((el) => el.role === item.role)
        }))
    )

    const onHandleEditState = (editMode: TEditMode) =>
        setState((prev) => prev.map((item) => (item.login === login.value ? { ...item, editMode } : item)))

    const onCloseEditMode = () => {
        onInitializeFields()
        onHandleEditState('none')
    }

    const onEditPassword = () => {
        onHandleEditState('password')
    }

    const onEditRoles = () => {
        onHandleEditState('role')
    }

    const onHandlerUpdateAdmin = () => {
        onUpdateAdminCallback({ newPassword: newPassword.value, login: login.value })
        onCloseEditMode()
    }

    const actions: Record<TEditMode, IAction[]> = {
        none: [
            {
                title: 'Управление доступами',
                callback: onEditRoles,
                className: 'chancgeRoles',
                disabled: isFetchingAdmins,
                isVisible: roles.every((r) => r.role !== ROLE_MAPPER.SADMN.role)
            },
            {
                title: 'Сменить пароль',
                callback: onEditPassword,
                className: 'chancgePassword',
                disabled: isFetchingAdmins,
                isVisible: true
            },
            {
                title: 'Удалить',
                callback: () => setRemovableAdmin(login.value),
                className: 'remove',
                disabled: isFetchingAdmins,
                isVisible: roles.every((r) => r.role !== ROLE_MAPPER.SADMN.role)
            }
        ],
        password: [
            {
                title: 'Сохранить',
                callback: onHandlerUpdateAdmin,
                className: 'save',
                disabled: isValidFields || isFetchingAdmins,
                isVisible: true
            }
        ],
        role: [
            // {
            //     title: 'Сохранить',
            //     callback: () => {},
            //     className: 'save',
            //     disabled: false
            // }
        ]
    }

    const currentActions = actions[editMode].filter((item) => item.isVisible)

    return (
        <div className={classNames(style.container, editMode !== 'none' && style.unbluredContainer)}>
            <div className={style.header}>
                <span>{`Admin_${index}`}</span>
                {editMode !== 'none' && (
                    <div className={style.close} onClick={onCloseEditMode}>
                        <Icon iconName={EIcons.CLOS} color={'#161F29'} />
                    </div>
                )}
            </div>
            <div className={style.fields}>
                <CustomField
                    name={login.name}
                    value={login.value}
                    onChange={onChangeLogin}
                    placeholder={login.placeholder}
                    onFocus={onTouchLogin}
                    error={loginError}
                    disabled={true}
                />
                {editMode === 'role' && (
                    <Roles
                        onAddAdminRole={onAddAdminRole}
                        onRemoveAdminRole={onRemoveAdminRole}
                        login={loginVal}
                        roles={adminRoles}
                        setRoles={setAdminRoles}
                    />
                )}
                {editMode === 'password' && (
                    <CustomField
                        name={newPassword.name}
                        value={newPassword.value}
                        onChange={onChangeNewPassword}
                        placeholder={newPassword.placeholder}
                        onFocus={onTouchNewPassword}
                        error={newPasswordError}
                        onZeroText={onZeroNewPassword}
                    />
                )}
                {editMode === 'password' && (
                    <CustomField
                        name={confirmPassword.name}
                        value={confirmPassword.value}
                        onChange={onChangeConfirmPassword}
                        placeholder={confirmPassword.placeholder}
                        onFocus={onTouchConfirmPassword}
                        error={confirmPasswordError}
                        onZeroText={onZeroConfirmPassword}
                    />
                )}
            </div>
            <div className={style.actions}>
                {currentActions.map((action) => {
                    const { callback, className, title, disabled } = action
                    return (
                        <Button
                            key={title}
                            className={classNames(style.btn, style[className])}
                            onClick={callback}
                            disabled={disabled}
                        >
                            {title}
                        </Button>
                    )
                })}
            </div>
        </div>
    )
}
